import { getSkillProgress, MAX_SKILL_LEVEL, type SkillSnapshot } from '@dreamgame/shared';
import { tv } from 'tailwind-variants';
import type { PawnData } from '../../state/entities';
import { skillName, t } from '../../state/i18n';
import { getSkillTotalXp } from '../../state/skillXp';

const statsPanel = tv({
  slots: {
    root: 'flex flex-col',
    title: [
      'text-center text-rs-gold font-semibold text-[0.95em] mb-[0.85em] tracking-wide',
      'border-b border-rs-stone-light/30 pb-[0.55em]',
    ],
    skillsList: 'flex flex-col gap-[0.3em]',
    capacitiesSection: ['mt-[0.8em] pt-[0.55em]', 'border-t border-rs-stone-light/30'],
    sectionLabel: ['text-[0.78em] uppercase tracking-wider text-rs-parchment-dark mb-[0.5em]'],
  },
});

const skillRow = tv({
  base: [
    'flex items-center gap-[0.55em]',
    'py-[0.3em] px-[0.5em]',
    'bg-rs-stone-dark/40',
    'rounded-[0.25em]',
    'group',
    'hover:bg-rs-stone-dark/60',
    'transition-colors',
  ],
});

const skillIcon = tv({
  base: ['w-[1.2em] h-[1.2em] flex items-center justify-center', 'text-[0.75em] font-bold'],
  variants: {
    category: {
      combat: 'text-red-400',
      gathering: 'text-amber-400',
      craft: 'text-emerald-400',
      knowledge: 'text-purple-400',
      social: 'text-sky-400',
    },
  },
});

const skillNameStyle = tv({
  base: 'flex-1 text-[0.84em] text-rs-parchment',
});

const skillLevel = tv({
  base: 'text-[0.84em] font-bold w-[1.2em] text-center',
  variants: {
    level: {
      none: 'text-rs-stone-light/40',
      low: 'text-rs-parchment-dark',
      mid: 'text-rs-parchment',
      high: 'text-rs-gold',
      master: 'text-rs-gold animate-pulse-gold',
    },
  },
});

const skillRow_unimplemented = tv({
  base: [
    'flex items-center gap-[0.55em]',
    'py-[0.3em] px-[0.5em]',
    'bg-rs-stone-dark/20',
    'rounded-[0.25em]',
    'opacity-40',
  ],
});

const skillBar = tv({
  base: 'w-[5em] h-[0.38em] bg-rs-stone-dark rounded overflow-hidden',
});

const skillFill = tv({
  base: 'h-full rounded transition-all duration-300',
  variants: {
    category: {
      combat: 'bg-gradient-to-r from-red-600 to-red-400',
      gathering: 'bg-gradient-to-r from-amber-600 to-amber-400',
      craft: 'bg-gradient-to-r from-emerald-600 to-emerald-400',
      knowledge: 'bg-gradient-to-r from-purple-600 to-purple-400',
      social: 'bg-gradient-to-r from-sky-600 to-sky-400',
    },
  },
});

const capacityRow = tv({
  base: 'flex items-center justify-between text-[0.84em] mb-[0.28em]',
});

const capacityLabel = tv({
  base: 'text-rs-parchment-dark',
});

const capacityValue = tv({
  base: 'font-medium',
  variants: {
    status: {
      good: 'text-emerald-400',
      okay: 'text-rs-parchment',
      poor: 'text-amber-400',
      bad: 'text-red-400',
    },
  },
});

const getLevelVariant = ({
  level,
}: {
  level: number;
}): 'none' | 'low' | 'mid' | 'high' | 'master' => {
  if (level === 0) {
    return 'none';
  }
  if (level <= 3) {
    return 'low';
  }
  if (level <= 6) {
    return 'mid';
  }
  if (level <= 9) {
    return 'high';
  }
  return 'master';
};

const IMPLEMENTED_SKILLS = new Set([
  'melee',
  'shooting',
  'toughness',
  'mining',
  'woodcutting',
  'fishing',
  'cooking',
  'construction',
  'metalwork',
]);

interface SkillRowProps {
  pawnNetworkId: number;
  skill: SkillSnapshot;
}

const SkillRowComponent = ({ pawnNetworkId, skill }: SkillRowProps) => {
  const isImplemented = IMPLEMENTED_SKILLS.has(skill.id);
  const localizedName = skillName({ skillId: skill.id });

  if (!isImplemented) {
    return (
      <div class={skillRow_unimplemented()} title={t('skills.comingSoon')}>
        <span class={skillIcon({ category: skill.category })} />
        <span class={skillNameStyle()}>{localizedName}</span>
        <span class="text-[0.72em] text-rs-stone-light/50 italic">{t('skills.comingSoon')}</span>
      </div>
    );
  }

  const levelPercent = Math.min(100, (skill.level / 10) * 100);
  const aptitudeSymbol = skill.aptitude === 2 ? '++' : skill.aptitude === 1 ? '+' : '';
  const totalXp = getSkillTotalXp({
    pawnNetworkId,
    skillId: skill.id,
    level: skill.level,
  });
  const progress = getSkillProgress({ totalXp });
  const xpRemaining = Math.max(0, progress.xpToNextLevel - progress.currentXp);
  const xpToNextText =
    progress.level >= MAX_SKILL_LEVEL
      ? t('ui.tooltips.skillMaxLevel')
      : t('ui.tooltips.skillXpToNext', {
          xp: xpRemaining.toLocaleString(),
        });
  const tooltip = [
    t('ui.tooltips.skillCurrentXp', {
      current: progress.currentXp.toLocaleString(),
      required: progress.xpToNextLevel.toLocaleString(),
    }),
    xpToNextText,
  ].join('\n');

  return (
    <div class={skillRow()} title={tooltip}>
      <span class={skillIcon({ category: skill.category })}>{aptitudeSymbol}</span>
      <span class={skillNameStyle()}>{localizedName}</span>
      <div class={skillBar()}>
        <div
          class={skillFill({ category: skill.category })}
          style={{ width: `${levelPercent}%` }}
        />
      </div>
      <span class={skillLevel({ level: getLevelVariant({ level: skill.level }) })}>
        {skill.level}
      </span>
    </div>
  );
};

// Capacities based on pawn health
interface CapacityData {
  readonly name: string;
  readonly value: number;
}

const getCapacities = ({ pawn }: { pawn: PawnData }): readonly CapacityData[] => {
  const healthPercent = (pawn.health / pawn.maxHealth) * 100;
  return [
    { name: t('ui.labels.mobility'), value: healthPercent > 30 ? 100 : healthPercent * 3 },
    { name: t('ui.labels.dexterity'), value: 100 },
    { name: t('ui.labels.awareness'), value: healthPercent > 50 ? 100 : healthPercent * 2 },
  ];
};

const getCapacityStatus = ({ value }: { value: number }): 'good' | 'okay' | 'poor' | 'bad' => {
  if (value >= 100) {
    return 'good';
  }
  if (value >= 70) {
    return 'okay';
  }
  if (value >= 40) {
    return 'poor';
  }
  return 'bad';
};

interface StatsPanelProps {
  pawn: PawnData;
}

export const StatsPanel = ({ pawn }: StatsPanelProps) => {
  const styles = statsPanel();
  const skills = pawn.skills;
  const capacities = getCapacities({ pawn });

  return (
    <div class={styles.root()}>
      <div class={styles.title()}>{t('ui.panels.skills')}</div>

      {/* Skills List */}
      <div class={styles.skillsList()}>
        {skills
          .filter((skill) => IMPLEMENTED_SKILLS.has(skill.id))
          .map((skill) => (
            <SkillRowComponent key={skill.id} pawnNetworkId={pawn.networkId} skill={skill} />
          ))}
      </div>

      {/* Capacities */}
      <div class={styles.capacitiesSection()}>
        <div class={styles.sectionLabel()}>{t('ui.labels.capacities')}</div>
        {capacities.map((cap) => (
          <div key={cap.name} class={capacityRow()}>
            <span class={capacityLabel()}>{cap.name}</span>
            <span class={capacityValue({ status: getCapacityStatus({ value: cap.value }) })}>
              {Math.round(cap.value)}%
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};
